import * as yup from 'yup';
import { Button, Caption1, Caption2, makeStyles } from '@fluentui/react-components';
import { SendRegular, DismissRegular, InfoRegular, Info12Regular } from '@fluentui/react-icons';
import { UseFormReturn, Controller } from 'react-hook-form';

import MentionsTextarea from './MentionsTextarea';
import { BaseSyntheticEvent, FC } from 'react';
import { ControlledField } from '@shared/components';

export const PARSE_DRAFT_CHECKBOX_LABEL_CLASSNAME = 'fui-Checkbox__label';

export const schemaComment = yup.object({
  text: yup.string().required('Add a comment.'),
  parseDraft: yup.boolean(), // "Add to draft" option feature. See PRD-94 for more details.
});

export type TFormDataComment = yup.InferType<typeof schemaComment>;

const useStyles = makeStyles({
  button: {
    paddingLeft: '20px',
    paddingRight: '20px',
  },
  parseDraftCheckbox: {
    marginLeft: '-4px',
    height: '24px',
    '& div': {
      margin: '4px',
    },
  },
});

type TProps = {
  // the entire useForm object
  form: UseFormReturn<TFormDataComment>;
  // is the textarea focused?
  isFocused?: boolean;
  setIsFocused?: (focused: boolean) => void;

  // from the parent's useForm
  isValid: boolean;
  onSubmit: (e?: BaseSyntheticEvent) => void;
  onDismiss: () => void;

  autoFocus?: boolean;

  withParseDraftCheckbox?: boolean; // "Add to draft" option feature. See PRD-94 for more details.
  infoText?: string;
};

const CommentForm: FC<TProps> = ({
  form,
  isFocused = true,
  setIsFocused = () => {},
  isValid,
  onSubmit,
  onDismiss,
  autoFocus = true,
  withParseDraftCheckbox = false,
  infoText,
}) => {
  const {
    control,
    watch,
    formState: { errors },
  } = form;

  const textValue = watch('text');

  const showButtons = isFocused || textValue.length > 0;

  const classes = useStyles();

  return (
    <form onSubmit={onSubmit} className="flex flex-col space-y-2 w-full">
      <Controller
        name="text"
        control={control}
        render={({ field }) => (
          <MentionsTextarea
            value={field.value}
            onChange={field.onChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => {
              setIsFocused(false);
              // do not call onDismiss() here
              field.onBlur();
            }}
            autoFocus={autoFocus}
          />
        )}
      />

      {errors.text && <p className="text-red-500">{errors.text.message as string}</p>}

      {withParseDraftCheckbox && (
        <ControlledField
          label="Add to draft"
          name="parseDraft"
          control={control}
          errors={errors}
          inputType="checkbox"
          onClick={(e: any) => e.stopPropagation()} // Avoid unselecting the comment card
          className={classes.parseDraftCheckbox}
        />
      )}

      {infoText && (
        <Caption2>
          <Info12Regular className="mb-0.5 mr-px" />
          {infoText}
        </Caption2>
      )}

      {showButtons && (
        <div className="flex justify-end space-x-1">
          <Button
            appearance="secondary"
            size="small"
            icon={<DismissRegular />}
            onClick={(e) => {
              // avoid unselecting the comment card
              e.stopPropagation();
              onDismiss();
            }}
            className={classes.button}
            aria-label="dismiss"
          />
          <Button
            type="submit"
            appearance="primary"
            size="small"
            icon={<SendRegular />}
            disabled={!isValid}
            className={classes.button}
            onClick={(e) => e.stopPropagation()} // avoid unselecting the comment card
            aria-label="send"
          />
        </div>
      )}
    </form>
  );
};

export default CommentForm;
